/**
 * Audit Log Section Component
 */

import { Clock, User, Globe, Monitor, Key, MapPin } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuditLogs } from '../hooks/useInstallationJobs';

interface AuditLogSectionProps {
  jobId: string;
}

const ACTION_COLORS: Record<string, string> = {
  access_granted: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  access_denied: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
  checklist_updated: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  photo_uploaded: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
  signature_captured: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
  job_submitted: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  job_reviewed: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  sensor_verified: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  verification_completed: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  code_generated: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
  code_revoked: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
  code_sent: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
};

const ACTION_LABELS: Record<string, string> = {
  access_granted: 'Access Granted',
  access_denied: 'Access Denied',
  checklist_updated: 'Checklist Updated',
  photo_uploaded: 'Photo Uploaded',
  signature_captured: 'Signature Captured',
  job_submitted: 'Job Submitted',
  job_reviewed: 'Job Reviewed',
  sensor_verified: 'Sensor Verified',
  verification_completed: 'Verification Completed',
  code_generated: 'Code Generated',
  code_revoked: 'Code Revoked',
  code_sent: 'Code Sent',
};

export function AuditLogSection({ jobId }: AuditLogSectionProps) {
  const { data, isLoading, isError } = useAuditLogs(jobId);

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleString('en-CA');
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Audit Log</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} className="h-20 w-full" />
          ))}
        </CardContent>
      </Card>
    );
  }

  if (isError) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Audit Log</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground text-center py-8">
            Failed to load audit logs. Please try again.
          </p>
        </CardContent>
      </Card>
    );
  }

  const logs = data?.logs || [];

  return (
    <Card>
      <CardHeader>
        <CardTitle>Audit Log</CardTitle>
        <CardDescription>
          All access and actions performed on this job are logged for compliance
        </CardDescription>
      </CardHeader>
      <CardContent>
        {logs.length === 0 ? (
          <p className="text-muted-foreground text-center py-8">
            No audit log entries yet.
          </p>
        ) : (
          <div className="space-y-4">
            {logs.map((log) => (
              <div
                key={log.id}
                className="flex items-start gap-4 p-4 rounded-lg border bg-card"
              >
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                    {log.action.includes('access') ? (
                      <Key className="h-5 w-5 text-muted-foreground" />
                    ) : log.action.includes('checklist') || log.action.includes('photo') || log.action.includes('signature') ? (
                      <Monitor className="h-5 w-5 text-muted-foreground" />
                    ) : (
                      <Globe className="h-5 w-5 text-muted-foreground" />
                    )}
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge className={ACTION_COLORS[log.action] || 'bg-gray-100 text-gray-800'}>
                      {ACTION_LABELS[log.action] || log.action}
                    </Badge>
                    <span className="text-xs text-muted-foreground flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {formatDate(log.timestamp)}
                    </span>
                  </div>
                  <div className="mt-2 space-y-1">
                    {log.technicianName && (
                      <p className="text-sm flex items-center gap-1">
                        <User className="h-3 w-3 text-muted-foreground" />
                        <span className="font-medium">{log.technicianName}</span>
                      </p>
                    )}
                    {log.performedByName && !log.technicianName && (
                      <p className="text-sm flex items-center gap-1">
                        <User className="h-3 w-3 text-muted-foreground" />
                        <span className="font-medium">{log.performedByName}</span>
                        <span className="text-muted-foreground">(Admin)</span>
                      </p>
                    )}
                    <div className="flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
                      {log.ipAddress && (
                        <span className="flex items-center gap-1">
                          <Globe className="h-3 w-3" />
                          {log.ipAddress}
                        </span>
                      )}
                      {log.latitude && log.longitude && (
                        <a
                          href={`https://www.google.com/maps?q=${log.latitude},${log.longitude}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 text-blue-600 hover:text-blue-800 hover:underline"
                          title={`Accuracy: ${log.locationAccuracy ? `±${Math.round(log.locationAccuracy)}m` : 'unknown'}`}
                        >
                          <MapPin className="h-3 w-3" />
                          GPS: {log.latitude.toFixed(5)}, {log.longitude.toFixed(5)}
                        </a>
                      )}
                      {log.accessCodeHash && (
                        <span className="flex items-center gap-1">
                          <Key className="h-3 w-3" />
                          Code: {log.accessCodeHash.substring(0, 8)}...
                        </span>
                      )}
                    </div>
                    {log.details && Object.keys(log.details).length > 0 && (
                      <details className="mt-2">
                        <summary className="text-xs text-muted-foreground cursor-pointer hover:text-foreground">
                          View details
                        </summary>
                        <pre className="mt-1 p-2 text-xs bg-muted rounded overflow-auto">
                          {JSON.stringify(log.details, null, 2)}
                        </pre>
                      </details>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
